import React from 'react';
import ReactDOM from 'react-dom/client';
import * as serviceWorker from './serviceWorker';

import 'bootstrap/dist/css/bootstrap.css';
import 'bootstrap-icons/font/bootstrap-icons.css';
import 'typeface-montserrat';
import '@fortawesome/fontawesome-free/css/all.css'
import 'mapbox-gl/dist/mapbox-gl.css';


import App from './App';

import attachStylesheet from "core/utils/attach-stylesheet";
import attachScript from "core/utils/attach-script";

const DEFAULT_SKIN = "default";

// Company API's am_color_scheme.code is used directly as the public/skins/<name>
// folder name, so the two need to stay in sync (e.g. code "rrd" -> public/skins/rrd/).
function getActiveCompanySkinCode() {
  try {
    return JSON.parse(localStorage.getItem("am:audience:active:company") || "{}").color_scheme?.code;
  } catch {
    return null;
  }
}

// attachStylesheet/attachScript only ever append - if loadSkin() runs more than
// once in the same page instance (the timeout-fallback path below), a previous
// attempt's <style>/<script> can be left behind even after a later one succeeds.
// CSS custom properties from a stale stylesheet keep cascading (window.skin/logo
// gets correctly overwritten by the later script, but colors don't), so always
// clear out any previously-injected skin assets before attaching a new one.
function removePreviousSkinAssets() {
  document.querySelectorAll('script[src*="/skins/"]').forEach((el) => el.remove());
  document.querySelectorAll('style').forEach((el) => {
    if (el.textContent.includes('/skins/')) el.remove();
  });
}

// attachScript/attachStylesheet poll indefinitely with no timeout - if a skin's
// files 404 (e.g. its public/skins/<name> folder gets renamed/removed), window.skin
// never gets set and the app would otherwise hang on a blank page forever. Race
// against a timeout and fall back to DEFAULT_SKIN so a bad/stale skin name can't
// take down the whole app.
function loadSkin(skinName) {
  removePreviousSkinAssets();
  window.skin_root = `${process.env.PUBLIC_URL}/skins/${skinName}`;
  return Promise.all([
    attachStylesheet(`${window.skin_root}/style.css`),
    attachScript(`${window.skin_root}/init.js`, () => !!window.skin)
  ]);
}

function withTimeout(promise, ms) {
  return Promise.race([
    promise,
    new Promise((_, reject) => setTimeout(() => reject(new Error(`timed out after ${ms}ms`)), ms))
  ]);
}

const requestedSkin = getActiveCompanySkinCode() || window.AM_SKIN_NAME || DEFAULT_SKIN;

withTimeout(loadSkin(requestedSkin), 5000)
  .catch((error) => {
    if (requestedSkin === DEFAULT_SKIN) {
      // nothing left to fall back to - render anyway rather than hang forever.
      console.error(`White-label skin "${DEFAULT_SKIN}" failed to load, rendering without it.`, error);
      return;
    }
    console.error(`White-label skin "${requestedSkin}" failed to load, falling back to "${DEFAULT_SKIN}".`, error);
    return withTimeout(loadSkin(DEFAULT_SKIN), 5000).catch((fallbackError) => {
      // both attempts failed - nothing left to fall back to, render anyway rather than hang forever.
      console.error(`White-label skin "${DEFAULT_SKIN}" also failed to load, rendering without it.`, fallbackError);
    });
  })
  .then(() => {
    const root = ReactDOM.createRoot(document.getElementById('root'));
    root.render(
      <App />
    );
  });

serviceWorker.unregister();
